import React from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon } from '../../../components/jds/icons';
import { JdsTag } from '../../../components/jds/JdsTag';
import { JdsSkeleton } from '../../../components/jds/JdsSkeleton';
import { TweenedInteger } from '../../../components/jds/TweenedCurrency';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { buildComparisonRows, formatCycle } from '../../PaymentPlans/utils';
import '../../../components/jds/jds.css';

// Mesmos itens/benefícios do comparativo de planos, para um plano só. Painel
// fixo ao lado do formulário (sticky), acompanhando as mudanças em tempo real.
// O <aside> existe mesmo sem plano (placeholder) e durante o carregamento
// (skeleton): o espaço fica reservado e o card principal não é empurrado.
export function PlanDetailsPanel({
  plan,
  loading = false,
  addonQuantities = {},
}) {
  if (loading || !plan) {
    return (
      <aside className="jds-card jds-side-panel" aria-label="Detalhes do plano">
        {loading ? (
          <div role="status" aria-label="Carregando plano">
            <JdsSkeleton width="60%" height={24} style={{ marginBottom: 12 }} />
            <JdsSkeleton width="40%" height={16} style={{ marginBottom: 24 }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <JdsSkeleton key={i} height={14} style={{ marginBottom: 20 }} />
            ))}
          </div>
        ) : (
          <p className="text-muted mb-0">
            Selecione um plano para ver os itens incluídos.
          </p>
        )}
      </aside>
    );
  }

  const rows = buildComparisonRows([plan]);

  return (
    <aside className="jds-card jds-side-panel" aria-label="Detalhes do plano">
      {/* key: trocar de plano faz o conteúdo entrar de novo (fade) */}
      <div key={plan.id} className="jds-fade">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h2 className="jds-title-md">{plan.name}</h2>
            <div
              className="d-flex align-items-center mt-2"
              style={{ gap: 'var(--space-8)' }}
            >
              <span
                style={{
                  color: 'var(--fg-brand-strong)',
                  fontWeight: 'var(--fw-semibold)',
                }}
              >
                {formatCurrency(plan.amount)}
              </span>
              <JdsTag color="brand" label={formatCycle(plan.cycleDays)} />
              <JdsTag
                dot
                color={plan.active ? 'success' : 'error'}
                label={plan.active ? 'Ativo' : 'Inativo'}
              />
            </div>
          </div>
        </div>

        <h3 className="jds-title-xs mb-2">Itens incluídos</h3>
        {rows.length === 0 ? (
          <p className="text-muted mb-0">Este plano não possui itens.</p>
        ) : (
          rows.map((row) => {
            const addonQuantity = addonQuantities[row.id];
            return (
              <div
                key={row.code}
                className="jds-detail-row"
                style={{
                  background: addonQuantity
                    ? 'var(--bg-brand-subtler)'
                    : 'transparent',
                  boxShadow: addonQuantity
                    ? 'inset 4px 0 0 var(--border-brand-default)'
                    : 'inset 0 0 0 transparent',
                  paddingLeft: addonQuantity ? 'var(--space-12)' : 0,
                  transition:
                    'background-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out), padding-left var(--dur-base) var(--ease-out)',
                }}
              >
                <span className="text-muted">
                  <Link
                    to={`/payments/items/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted mr-2"
                    aria-label={`Abrir detalhes de ${row.name} em nova guia`}
                    title="Abrir em nova guia"
                  >
                    <EyeIcon />
                  </Link>
                  {row.name}
                  {addonQuantity > 0 && (
                    <>
                      {' '}
                      <span
                        className="jds-pop"
                        style={{ display: 'inline-block' }}
                      >
                        <JdsTag color="brand" label="Produto nesta oferta" />
                      </span>
                    </>
                  )}
                </span>
                <span>
                  {row.values[plan.id]}
                  {addonQuantity > 0 && (
                    <span
                      style={{
                        color: 'var(--fg-brand-strong)',
                        fontWeight: 'var(--fw-semibold)',
                      }}
                    >
                      {' '}
                      + <TweenedInteger value={addonQuantity} />
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
